import type { FC, ReactNode } from 'react'
import type { Course } from '@/types'

import { ArrowRight, BookOpen } from 'lucide-react'
import { ButtonLink } from '@/components/generals/button-link'
import { Container } from '@/components/generals/container'
import { SectionHeading } from '@/components/generals/section-heading'
import { CourseCard } from '@/components/courses/course-card'

interface CoursesSectionProps {
    courses: Array<Course>
}

export const CoursesSection: FC<CoursesSectionProps> = ({ courses }): ReactNode => {
    return (
        <section id='courses' aria-labelledby='courses-title' className='py-20 sm:py-28'>
            <Container>
                <div className='flex flex-col gap-6 md:flex-row md:items-end md:justify-between'>
                    <SectionHeading
                        id='courses-title'
                        eyebrow="Popular courses"
                        title="Find the right course for where you are today"
                        description="Practical courses for every level, from your very first click to writing real code. Every course runs in class and live online."
                    />

                    <ButtonLink href='/courses' variant='outline' className='self-start md:self-auto'>
                        View all courses
                        <ArrowRight aria-hidden='true' />
                    </ButtonLink>
                </div>

                {courses.length > 0
                    ? (
                        <div className='mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
                            {courses.map(course => (
                                <CourseCard key={course.id} course={course} />
                            ))}
                        </div>
                    )
                    : (
                        <div className='mt-12 flex flex-col items-center rounded-3xl border border-dashed bg-card px-6 py-14 text-center'>
                            <span className='flex size-12 items-center justify-center rounded-2xl bg-secondary text-primary'>
                                <BookOpen className='size-6' aria-hidden='true' />
                            </span>

                            <h3 className='mt-5 text-xl font-semibold'>
                                New courses are on their way
                            </h3>

                            <p className='mt-2 max-w-md text-muted-foreground'>
                                We are updating our course list. Send us a message and an advisor will tell you what is starting next.
                            </p>

                            <ButtonLink href='/enroll' className='mt-6'>
                                Ask an advisor
                            </ButtonLink>
                        </div>
                    )}
            </Container>
        </section>
    )
}
